import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from './supabase-data/LoadingAndError';
import { getUserLocation } from '@/utils/userSettingsUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ALL_ZMANIM, formatTime, calculateZman } from '@/utils/zmanimUtils';
import { Button } from '@/components/ui/button';
import { List, Grid } from 'lucide-react';
import ZmanimCardsPage from './ZmanimCardsPage';

const ZmanimPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [zmanim, setZmanim] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<boolean>(!isMobile);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');

  useEffect(() => {
    const fetchZmanim = async () => {
      try {
        const location = await getUserLocation();
        if (!location.lat || !location.lng) {
          setError('לא הוגדר מיקום');
          setLoading(false);
          return;
        }
        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(
          `https://www.hebcal.com/zmanim?cfg=json&date=${today}&latitude=${location.lat}&longitude=${location.lng}&timezone=${location.tzid || 'Asia/Jerusalem'}`
        );
        if (!response.ok) throw new Error('Failed to fetch zmanim');
        const data = await response.json();
        const times = data.times || {};
        const zmanimBase = {
          alot: times.alot || '',
          sunrise: times.sunrise || '',
          sunset: times.sunset || '',
          tzeit: times.tzeit || '',
        };
        setZmanim({ ...zmanimBase, date: today });
        setError(null);
      } catch (err) {
        console.error('Error fetching zmanim:', err);
        setError('שגיאה בטעינת זמני היום');
      } finally {
        setLoading(false);
      }
    };
    fetchZmanim();
  }, []);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${isMobile ? 'h-48' : 'h-64'}`}>
        <LoadingSpinner isLoading={loading} />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={isMobile ? 'p-2' : 'max-w-4xl mx-auto p-6'}>
        <Alert className="mb-6">
          <AlertTitle>שגיאה</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{error}</p>
            {error === 'לא הוגדר מיקום' && (
              <p>יש להגדיר מיקום בלוח הבקרה כדי להציג את זמני היום.</p>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (!zmanim) {
    return null;
  }

  // Get the most important zmanim for collapsed mobile view
  const importantZmanim = ALL_ZMANIM.filter(z => 
    ['sunrise', 'sunset', 'shema_mga', 'mincha_gedola'].includes(z.key)
  );

  if (viewMode === 'cards') {
    return <ZmanimCardsPage />;
  }

  return (
    <div className={isMobile ? 'p-2' : 'max-w-4xl mx-auto p-6'}>
      <Card className="overflow-hidden">
        <CardHeader className="pb-2 flex flex-row justify-between items-center">
          <CardTitle className="text-center text-xl sm:text-2xl font-bold">זמני היום</CardTitle>
          <div className="flex items-center gap-2">
            {isMobile && (
              <button 
                onClick={() => setExpanded(!expanded)}
                className="text-sm text-blue-600 font-medium"
              >
                {expanded ? 'הצג פחות' : 'הצג הכל'}
              </button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewMode(viewMode === 'list' ? 'cards' : 'list')}
              className="ml-2"
            >
              {viewMode === 'list' ? <Grid className="h-5 w-5" /> : <List className="h-5 w-5" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isMobile && !expanded ? (
            // Collapsed mobile view with just key zmanim
            <div className="grid grid-cols-2 gap-y-3 gap-x-4">
              {importantZmanim.map(({ key, label }) => (
                <div key={key} className="flex justify-between items-center border-b pb-1">
                  <span className="font-medium text-sm">{label}:</span>
                  <span className="text-sm">{formatTime(calculateZman(key, zmanim))}</span>
                </div>
              ))}
            </div>
          ) : (
            // Full view (desktop or expanded mobile)
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-y-2' : 'grid-cols-2 gap-4'}`}>
              {ALL_ZMANIM.map(({ key, label }) => (
                <div key={key} className={`flex justify-between items-center ${isMobile ? 'border-b pb-1' : ''}`}>
                  <span className={`font-semibold ${isMobile ? 'text-sm' : ''}`}>{label}:</span>
                  <span className={isMobile ? 'text-sm' : ''}>{formatTime(calculateZman(key, zmanim))}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ZmanimPage;
